import React, {
    useEffect,
    useState
} from "react";

import {
    CompetitionGame,
    Game,
    getGames
} from "../services/mexfbaApi";

import {
    canShowPregamePrediction,
    createGamePrediction,
    GamePrediction as PredictionResult
} from "../services/gamePrediction";

interface Props {
    game: CompetitionGame;
    refreshToken: number;
}

type PredictionState =
    | {
          status: "loading";
      }
    | {
          status: "unavailable";
      }
    | {
          status: "error";
      }
    | {
          status: "ready";
          prediction: PredictionResult;
      };

function mergeGameData(
    detailGame: CompetitionGame,
    indexedGame?: Game
): CompetitionGame {
    return {
        ...detailGame,

        /*
            En los datos compartidos, hora está en partidos.json.
            Si el detalle también la contiene, lo usamos como
            alternativa.
        */
        fecha:
            indexedGame?.fecha ||
            detailGame.fecha,

        hora:
            indexedGame?.hora ||
            detailGame.hora
    };
}

const GamePrediction: React.FC<Props> = function ({
    game,
    refreshToken
}) {
    const [state, setState] = useState<PredictionState>({
        status: "loading"
    });

    const [indexedGame, setIndexedGame] =
        useState<Game | undefined>();

    const [now, setNow] = useState<Date>(
        function () {
            return new Date();
        }
    );

    useEffect(
        function () {
            let cancelled = false;

            async function loadPrediction() {
                setState({
                    status: "loading"
                });

                try {
                    const games = await getGames(
                        refreshToken > 0
                    );

                    if (cancelled) {
                        return;
                    }

                    const match = games.find(
                        function (item) {
                            return (
                                String(item.id) ===
                                String(game.id)
                            );
                        }
                    );

                    const mergedGame = mergeGameData(
                        game,
                        match
                    );

                    const prediction = createGamePrediction(
                        mergedGame,
                        games
                    );

                    setIndexedGame(match);

                    if (!prediction) {
                        setState({
                            status: "unavailable"
                        });

                        return;
                    }

                    setState({
                        status: "ready",
                        prediction
                    });
                } catch (requestError) {
                    console.error(
                        "No fue posible calcular el pronóstico:",
                        requestError
                    );

                    if (!cancelled) {
                        setState({
                            status: "error"
                        });
                    }
                }
            }

            loadPrediction();

            return function () {
                cancelled = true;
            };
        },
        [
            game.id,
            game.id_comp,
            game.id_local,
            game.id_visita,
            game.fecha,
            game.hora,
            refreshToken
        ]
    );

    /*
        El cálculo no es en vivo: este temporizador únicamente
        sirve para ocultar la tarjeta cuando llega la hora
        programada de inicio.
    */
    useEffect(function () {
        const interval = window.setInterval(
            function () {
                setNow(new Date());
            },
            10_000
        );

        return function () {
            window.clearInterval(interval);
        };
    }, []);

    const displayGame = mergeGameData(
        game,
        indexedGame
    );

    if (
        !canShowPregamePrediction(
            displayGame,
            now
        )
    ) {
        return null;
    }

    const localName =
        game.nombre_local || "Local";

    const visitorName =
        game.nombre_visita || "Visitante";

    let localPercentage = 0;
    let visitorPercentage = 0;

    if (state.status === "ready") {
        localPercentage = Math.round(
            state.prediction.localProbability * 100
        );

        visitorPercentage =
            100 - localPercentage;
    }

    return (
        <section className="game-section">
            <div className="game-section__heading">
                <div>
                    <span>PRONÓSTICO</span>
                    <h2>Estimación previa</h2>
                </div>
            </div>

            <div className="game-prediction">
                {state.status === "loading" && (
                    <p className="game-prediction__message">
                        Calculando pronóstico...
                    </p>
                )}

                {state.status === "unavailable" && (
                    <p className="game-prediction__message">
                        Aún no hay suficiente historial para
                        comparar a estos equipos.
                    </p>
                )}

                {state.status === "error" && (
                    <p className="game-prediction__message">
                        No fue posible calcular el pronóstico.
                        Intenta actualizar el partido.
                    </p>
                )}

                {state.status === "ready" && (
                    <>
                        <div className="game-prediction__teams">
                            <div>
                                <span>{localName}</span>

                                <strong>
                                    {localPercentage}%
                                </strong>
                            </div>

                            <div>
                                <span>{visitorName}</span>

                                <strong>
                                    {visitorPercentage}%
                                </strong>
                            </div>
                        </div>

                        <div
                            className="game-prediction__bar"
                            role="img"
                            aria-label={
                                `Estimación comparativa: ` +
                                `${localName} ${localPercentage} %; ` +
                                `${visitorName} ${visitorPercentage} %.`
                            }
                        >
                            <div
                                className="game-prediction__bar-local"
                                style={{
                                    width:
                                        `${
                                            state.prediction
                                                .localProbability * 100
                                        }%`
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default GamePrediction;
